'use client';

import useNotificationPlacementSetting from 'src/hooks/useNotificationPlacementSetting';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import NotificationComponent from './Notification';
import useNotificationStore, {
	Notification,
} from 'src/stores/useNotificationStore';
import { cj } from 'src/utils/classNames';
import { useEffect, useMemo, useRef, useState } from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { SEMANTIC_STATUS } from '../Utils/SemanticChip';
import AlertNotifications from 'src/components/Toasts/AlertNotifications/AlertNotifications';
import { partition } from 'lodash';
import { NotificationData } from 'src/utils/notifications';
import V3MarketOrderAlert from './MarketOrders/V3MarketOrderAlert';
import { Z_INDEX } from 'src/constants/zIndex';
import Button from 'src/components/primitives/Button/Button';
import ScrollShadows from 'src/components/ScrollShadows';
import {
	TOAST_ROUNDED_CLASSNAME,
	TOAST_SHADOW_CLASSNAME,
	TOAST_WIDTH_CLASSNAME,
} from 'src/components/Toasts/consts';

const COLLAPSED_NOTIFICATIONS_LIMIT = 3;

// The displayed notifications list is reversed, so we need to get the reverse index
const getReverseIndex = (index: number, length: number) => {
	return length - index - 1;
};

const GenericNotification = ({
	notification,
	index,
	hide,
}: {
	notification: Notification;
	index: number;
	hide: (notificationId?: string) => void;
}) => {
	const velocityStatus = useVelocityStore((s) => s.velocityStatus.mainStatus);

	const getNotificationComponent = (
		notificationItem: Notification,
		notificationIndex: number,
		statusInfo: {
			status: SEMANTIC_STATUS;
			message: string;
		}
	) => {
		switch (notificationItem.type) {
			case 'marketOrder':
				return (
					<V3MarketOrderAlert
						data={notificationItem}
						onClose={() => hide(notificationItem.id)}
					/>
				);
			default:
				return (
					<NotificationComponent
						key={`${notificationItem.startTimeMs}`}
						notificationData={notificationItem}
						index={notificationIndex}
						statusWarning={statusInfo.message}
						statusType={statusInfo.status}
					/>
				);
		}
	};

	return (
		<span key={index}>
			{getNotificationComponent(notification, index, velocityStatus)}
		</span>
	);
};

/**
 * This will show 3 notifications at a time, and will collapse the rest behind the 3rd toast.
 */
const CollapsedNotificationsList = ({
	notifications,
	hide,
}: {
	notifications: Notification[];
	hide: (notificationId?: string) => void;
}) => {
	const firstTwoNotifications = notifications.slice(0, 2);

	if (notifications.length < 3) {
		return null;
	}

	const notificationsBehindThird = notifications.slice(3, 5);

	return (
		<>
			{/** First 2 notifications */}
			{firstTwoNotifications.map((n, idx) => (
				<GenericNotification
					key={idx}
					index={getReverseIndex(idx, notifications.length)}
					notification={n}
					hide={hide}
				/>
			))}

			{/* Stacked notifications */}
			<div
				className={cj(
					'overflow-hidden pb-lg',
					// Reduce bottom padding when there's only one notification hidden in the stack.
					notificationsBehindThird.length === 1 && 'pb-md'
				)}
			>
				<div className={cj('relative', TOAST_ROUNDED_CLASSNAME)}>
					{/** 3rd notification */}
					<GenericNotification
						index={getReverseIndex(2, notifications.length)}
						notification={notifications[2]}
						hide={hide}
					/>

					{/* Hidden behind the 3rd notification. */}
					{notificationsBehindThird.map((n, idx) => (
						<div
							key={idx + 3}
							className={cj('absolute w-full', TOAST_ROUNDED_CLASSNAME)}
							style={{
								transform: `scale(${1 - 0.05 * (idx + 1)})`,
								bottom: -8 * (idx + 1),
								zIndex: -idx - 1,
							}}
						>
							<GenericNotification
								index={getReverseIndex(idx + 3, notifications.length)}
								notification={n}
								hide={hide}
							/>
						</div>
					))}
				</div>
			</div>
		</>
	);
};

const ToastList = () => {
	const isMobile = useIsMobileScreenSize();
	const notifications = useNotificationStore((s) => s.notifications);
	const clearAllNotifications = useNotificationStore(
		(s) => s.clearAllNotifications
	);
	const hideNotificationById = useNotificationStore(
		(s) => s.hideNotificationById
	);
	const [notificationSelection] = useNotificationPlacementSetting();

	const [userSelectedCollapsed, setUserSelectedCollapsed] = useState(true);

	const toastListContainerRef = useRef<HTMLDivElement>(null);

	const [topBarHeight, setTopBarHeight] = useState(48);
	const [footerHeight, setFooterHeight] = useState(28);

	useEffect(() => {
		function updateTopBarHeight() {
			const topBar = document.getElementById('topbar');
			if (topBar) {
				const { bottom } = topBar.getBoundingClientRect();
				setTopBarHeight(bottom);
			}
		}

		function updateFooterHeight() {
			const footer = document.getElementById('footer');
			if (footer) {
				const { height } = footer.getBoundingClientRect();
				setFooterHeight(Math.max(height, 26)); // 26px is the minimum height
			}
		}

		const renderMeasureTimeout = setTimeout(() => {
			updateTopBarHeight();
			updateFooterHeight();
		}, 500); // allow time for the topbar/footer to render

		// Optional: Add event listeners if the heights might change dynamically
		window.addEventListener('resize', updateTopBarHeight);
		window.addEventListener('resize', updateFooterHeight);

		return () => {
			clearTimeout(renderMeasureTimeout);
			window.removeEventListener('resize', updateTopBarHeight);
			window.removeEventListener('resize', updateFooterHeight);
		};
	}, []);

	const displayedNotifications = useMemo(
		() => [...notifications].reverse().filter((n) => !n.hidden),
		[notifications]
	);

	const [alertNotifications, genericNotifications] = useMemo(
		() => partition(displayedNotifications, (n) => n.type === 'alert'),
		[displayedNotifications]
	);

	const isNotificationsCollapsed =
		notifications.length > COLLAPSED_NOTIFICATIONS_LIMIT &&
		userSelectedCollapsed;
	const isViewAllNotifications =
		notifications.length > COLLAPSED_NOTIFICATIONS_LIMIT &&
		!userSelectedCollapsed;

	const notificationPositionStyles = useMemo(() => {
		if (isMobile) {
			return { bottom: 16, left: 0 };
		}

		if (notificationSelection === 'bottomLeft') {
			return { left: 0, bottom: footerHeight };
		}

		if (notificationSelection === 'bottomRight') {
			return { right: 0, bottom: footerHeight };
		}

		return { right: 0, top: topBarHeight };
	}, [isMobile, notificationSelection, footerHeight, topBarHeight]);

	const toastListContainerStyles = useMemo(() => {
		return {
			// 2x `topBarHeight` because the top starts from `topBarHeight`.
			maxHeight: `calc(100vh - ${topBarHeight * 2}px - ${footerHeight}px)`,
		};
	}, [topBarHeight, footerHeight]);

	// whenever the notification stack restarts, it should start from a collapsed state
	useEffect(() => {
		if (displayedNotifications.length === 0) {
			setUserSelectedCollapsed(true);
		}
	}, [displayedNotifications.length]);

	const hide = (notificationId?: string) => {
		if (notificationId) {
			hideNotificationById(notificationId);
		}
	};

	if (displayedNotifications.length === 0) {
		return null;
	}

	return (
		<div
			className={cj(
				'fixed flex w-auto flex-col gap-y-sm overflow-hidden px-sm py-xs',
				Z_INDEX.toastList
			)}
			style={notificationPositionStyles}
			data-testid="toast-list"
		>
			{/** Notifications List */}
			<ScrollShadows
				scrollableRef={toastListContainerRef}
				gradientFromClassName="from-container-bg"
				orientation="vertical"
				screenSize="all"
				isDisabled={!isViewAllNotifications}
			>
				<div
					className={cj(
						'flex w-full grow flex-col gap-y-sm',
						// `pb-xs` in order to not clobber the bottom shadow.
						'relative hidden-scroll overflow-y-auto pb-xs'
					)}
					style={toastListContainerStyles}
					ref={toastListContainerRef}
				>
					{/* Placement important: Alert Notifications always sit above other notifications. */}
					<AlertNotifications
						notifications={alertNotifications as NotificationData[]}
						footerHeight={footerHeight}
					/>
					{isNotificationsCollapsed ? (
						<CollapsedNotificationsList
							notifications={genericNotifications}
							hide={hide}
						/>
					) : (
						genericNotifications.map((n, idx) => (
							<GenericNotification
								key={idx}
								index={getReverseIndex(idx, notifications.length)}
								notification={n}
								hide={hide}
							/>
						))
					)}
				</div>
			</ScrollShadows>

			{/** Notifications List Actions */}
			{genericNotifications.length > 3 && (
				<div className={cj('grid grid-cols-2 gap-x-sm', TOAST_WIDTH_CLASSNAME)}>
					<Button
						size="md"
						className={TOAST_SHADOW_CLASSNAME}
						variant="stroke"
						onClick={() => clearAllNotifications()}
					>
						Clear All
					</Button>
					<Button
						size="md"
						className={TOAST_SHADOW_CLASSNAME}
						variant="secondary"
						onClick={() => setUserSelectedCollapsed(!userSelectedCollapsed)}
					>
						{userSelectedCollapsed ? 'View All' : 'Collapse All'} (
						{notifications.length})
					</Button>
				</div>
			)}
		</div>
	);
};

export default ToastList;
